import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu, SegmentedControl, Switch } from '@mantine/core';
import { useContext } from 'react';
import type { OrganizationResponse } from '../../../api/organization-client/organization-client.type';
import type { Portfolio } from '../../../api/portfolio-client/portfolio-client.type';
import { UserContext } from '../../../contexts';
import { useFeatureFlag } from '../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../helpers/feature-flags/feature-flags.type';
import { logout } from '../../../helpers/storage/storage';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { setSuperuserMode } from '../../../store/global-store/global-store.actions';
import { redBase } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';

interface AgentScopeMenuProps {
  organization: OrganizationResponse | null;
  organizations: OrganizationResponse[];
  portfolio: Portfolio | null;
  filteredPortfolios: Portfolio[];
  isDarkMode?: boolean;
  onOrgChange: (orgId: string) => void;
  onPortfolioChange: (portfolioId: string) => void;
  onToggleDarkMode: () => void;
  /** Called when the user clicks "Settings". */
  onSettings: () => void;
}

export function AgentScopeMenu({
  organization,
  organizations,
  portfolio,
  filteredPortfolios,
  isDarkMode,
  onOrgChange,
  onPortfolioChange,
  onToggleDarkMode,
  onSettings,
}: AgentScopeMenuProps) {
  const { user } = useContext(UserContext);
  const isDarkModeEnabled = useFeatureFlag(FeatureFlag.EnableFloraDarkMode);
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  return (
    <ScopeMenuDropdown>
      <Text
        size="tiny"
        weight="bold"
        color="var(--ink-lighter)"
        style={{ letterSpacing: '0.03em', padding: '6px 10px 4px' }}
      >
        {user?.email}
      </Text>
      <Divider color="var(--sky-light)" style={{ margin: '4px 0' }} />
      <ScopeMenuItemWrap>
        <ScopeMenuItem>
          <Icon name="corporate_fare" color="var(--ink-lighter)" size={18} />
          <Text size="small" color="var(--ink-dark)">
            {organization?.name ?? 'Organization'}
          </Text>
          <Icon name="chevron_right" color="var(--ink-lighter)" size={16} style={{ marginLeft: 'auto' }} />
        </ScopeMenuItem>
        <SubMenu className="sub-menu">
          <Text
            size="tiny"
            weight="bold"
            color="var(--ink-lighter)"
            style={{ textTransform: 'uppercase', letterSpacing: '0.03em', padding: '6px 10px 4px' }}
          >
            Organization
          </Text>
          {organizations.map((org) => (
            <ScopeOption key={org.id} $active={org.id === organization?.id} onClick={() => onOrgChange(org.id)}>
              <Text size="small" color="var(--ink-dark)">
                {org.name}
              </Text>
              {org.id === organization?.id && <Icon name="check" color="var(--secondary-base)" size={16} />}
            </ScopeOption>
          ))}
        </SubMenu>
      </ScopeMenuItemWrap>
      <ScopeMenuItemWrap>
        <ScopeMenuItem>
          <Icon name="folder_open" color="var(--ink-lighter)" size={18} />
          <Text size="small" color="var(--ink-dark)">
            {portfolio?.name ?? 'Portfolio'}
          </Text>
          <Icon name="chevron_right" color="var(--ink-lighter)" size={16} style={{ marginLeft: 'auto' }} />
        </ScopeMenuItem>
        <SubMenu className="sub-menu">
          <Text
            size="tiny"
            weight="bold"
            color="var(--ink-lighter)"
            style={{ textTransform: 'uppercase', letterSpacing: '0.03em', padding: '6px 10px 4px' }}
          >
            Portfolio
          </Text>
          {filteredPortfolios.map((p) => (
            <ScopeOption key={p.id} $active={p.id === portfolio?.id} onClick={() => onPortfolioChange(p.id)}>
              <Text size="small" color="var(--ink-dark)">
                {p.name}
              </Text>
              {p.id === portfolio?.id && <Icon name="check" color="var(--secondary-base)" size={16} />}
            </ScopeOption>
          ))}
        </SubMenu>
      </ScopeMenuItemWrap>
      <Divider color="var(--sky-light)" style={{ margin: '4px 0' }} />
      <ScopeMenuItem onClick={onSettings}>
        <Icon name="settings" color="var(--ink-lighter)" size={18} style={{ cursor: 'pointer' }} />
        <Text size="small" color="var(--ink-dark)">
          Settings
        </Text>
      </ScopeMenuItem>
      {isDarkModeEnabled && (
        <>
          <Divider color="var(--sky-light)" style={{ margin: '4px 0' }} />
          <ThemeToggleWrap>
            <SegmentedControl
              value={isDarkMode ? 'dark' : 'light'}
              onChange={() => onToggleDarkMode()}
              data={[
                {
                  label: (
                    <SegmentLabel>
                      <IconifyIcon icon="material-symbols:light-mode-outline" width={14} height={14} />
                      Light
                    </SegmentLabel>
                  ),
                  value: 'light',
                },
                {
                  label: (
                    <SegmentLabel>
                      <IconifyIcon icon="material-symbols:dark-mode-outline" width={14} height={14} />
                      Dark
                    </SegmentLabel>
                  ),
                  value: 'dark',
                },
              ]}
              size="xs"
              fullWidth
              styles={{
                root: {
                  background: 'var(--sky-lighter)',
                  borderRadius: 6,
                },
                label: {
                  color: 'var(--ink-dark)',
                  fontSize: 12,
                  padding: '4px 0',
                },
                indicator: {
                  background: 'var(--base-white)',
                  borderRadius: 5,
                  boxShadow: 'var(--box-shadow-small-soft)',
                },
              }}
            />
          </ThemeToggleWrap>
        </>
      )}
      {user?.is_superuser && (
        <>
          <Divider color="var(--sky-light)" style={{ margin: '4px 0' }} />
          <SuperuserToggleRow data-bf="flora-superuser-toggle">
            <Text size="small" weight="medium" color={redBase}>
              ENABLE SUPERUSER MODE
            </Text>
            <Switch
              aria-label="Enable superuser mode"
              color={redBase}
              size="sm"
              checked={superuserMode.enabled}
              onChange={() => {
                setSuperuserMode({
                  enabled: !superuserMode.enabled,
                  forceRecalculation: false,
                  showModelSelection: false,
                });
              }}
            />
          </SuperuserToggleRow>
        </>
      )}
      <Divider color="var(--sky-light)" style={{ margin: '4px 0' }} />
      <ScopeMenuItem onClick={() => logout()}>
        <Icon name="exit_to_app" color="var(--ink-lighter)" size={18} style={{ cursor: 'pointer' }} />
        <Text size="small" color="var(--ink-dark)">
          Sign Out
        </Text>
      </ScopeMenuItem>
    </ScopeMenuDropdown>
  );
}

const ScopeMenuDropdown = styled(Menu.Dropdown)`
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  padding: 6px;
  min-width: 240px;
  max-width: 240px;
`;

const ScopeMenuItemWrap = styled.div`
  position: relative;

  &:hover > .sub-menu {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
  }
`;

const ScopeMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: var(--sky-lighter);
  }
`;

const SubMenu = styled.div`
  position: absolute;
  left: calc(100% + 4px);
  bottom: 0;
  min-width: 200px;
  max-height: min(320px, calc(100vh - 40px));
  overflow-y: auto;
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  padding: 6px;
  box-shadow: var(--box-shadow-big-soft);
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transform: translateX(-4px);
  transition: opacity 0.12s, transform 0.12s;
  z-index: 1;
`;

const ThemeToggleWrap = styled.div`
  padding: 4px 6px 2px;
`;

const SuperuserToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;

  .mantine-Switch-track {
    cursor: pointer;
  }
`;

const SegmentLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`;

const ScopeOption = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--sky-lighter)' : 'transparent')};
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: var(--sky-lighter);
  }
`;
